import React, { createContext, Fragment, useContext, useRef } from "react";
import { Menu as HMenu, Transition } from "@headlessui/react";
import { cn } from "./cn";
import Kbd from "./Kbd";
import FloatingPanel from "./FloatingPanel";

const ContextMenuState = createContext(null);

/**
 * Bun dropdown menu. Spec: Component Library "Menus".
 * Panel: surface + hairline + radius 12 + menu shadow, 6px inner padding.
 * Items: 13/450, stone icon, mist hover; destructive items go danger with a
 * faint danger wash and sit below a divider.
 *
 *   <Menu button={<Button variant="secondary" iconOnly>...</Button>}>
 *     <Menu.Item icon={PencilIcon} shortcut="E" onClick={...}>Edit household</Menu.Item>
 *     <Menu.Divider />
 *     <Menu.Item icon={TrashIcon} danger onClick={...}>Archive household</Menu.Item>
 *   </Menu>
 */
export default function Menu({ button, align = "right", side = "bottom", width = 220, portal = false, className, children }) {
  const triggerRef = useRef(null);
  if (portal) {
    return (
      <HMenu as="div" className={cn("relative inline-block text-left font-instrument", className)}>
        {({ open }) => <>
          <HMenu.Button as={Fragment}>{React.cloneElement(button, { ref: triggerRef })}</HMenu.Button>
          {open && <FloatingPanel as={HMenu.Items} open autoFocus static tabIndex={-1} anchorRef={triggerRef} width={width} align={align === 'right' ? 'end' : 'start'} portalTarget={triggerRef.current?.closest('[data-bun-dialog-panel], [role="dialog"]')} className="p-[6px]" onKeyDown={(event) => {
            if (event.key !== "Enter" && event.key !== " ") return;
            const activeItem = event.currentTarget.ownerDocument.getElementById(event.currentTarget.getAttribute("aria-activedescendant"));
            if (activeItem?.dataset.bunMenuKeepOpen !== "true") return;
            event.preventDefault();
            activeItem.click();
          }}>
            {children}
          </FloatingPanel>}
        </>}
      </HMenu>
    );
  }
  return (
    <HMenu as="div" className={cn("relative inline-block text-left font-instrument", className)}>
      <HMenu.Button as={Fragment}>{button}</HMenu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="opacity-0 -translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-75"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <HMenu.Items
          onKeyDown={(event) => {
            if (event.key !== "Enter" && event.key !== " ") return;
            const activeItem = event.currentTarget.ownerDocument.getElementById(event.currentTarget.getAttribute("aria-activedescendant"));
            if (activeItem?.dataset.bunMenuKeepOpen !== "true") return;
            event.preventDefault();
            activeItem.click();
          }}
          style={{ width }}
          className={cn(
            "absolute z-40 p-[6px] bg-bun-surface border border-bun-line rounded-bun-lg shadow-bun-menu focus:outline-none",
            side === 'top' ? 'bottom-full mb-1' : 'mt-1',
            align === "right" ? "right-0 origin-top-right" : "left-0 origin-top-left"
          )}
        >
          {children}
        </HMenu.Items>
      </Transition>
    </HMenu>
  );
}

function Item({ icon: Icon, shortcut, trailing, danger, variant, disabled, closeOnSelect = true, onClick, className, children, ...props }) {
  const context = useContext(ContextMenuState);
  const primary = variant === "primary" && !danger;
  const renderItem = ({ active }) => (
        <button
          {...props}
          type="button"
          role={context ? 'menuitem' : undefined}
          tabIndex={context ? -1 : undefined}
          onClick={(event) => {
            if (!closeOnSelect) event.preventDefault();
            onClick?.(event);
            if (context && closeOnSelect) context.onDismiss?.('select', event);
          }}
          data-bun-menu-keep-open={!closeOnSelect || undefined}
          disabled={disabled}
          className={cn(
            "flex min-h-11 w-full items-center gap-[10px] px-[10px] py-2 rounded-[8px] text-left sm:min-h-0",
            "text-bun-body font-book cursor-pointer border-0 bg-transparent",
            danger ? "text-bun-danger" : "text-bun-text",
            active && (danger ? "bg-bun-danger-wash" : "bg-bun-mist"),
            context && !disabled && (danger ? "hover:bg-bun-danger-wash focus:bg-bun-danger-wash focus:outline-none" : "hover:bg-bun-mist focus:bg-bun-mist focus:outline-none"),
            primary && "bg-bun-ink text-bun-on-ink font-strong",
            primary && active && "bg-bun-ink-hover",
            disabled && "opacity-50 cursor-not-allowed",
            className
          )}
        >
          {Icon && <Icon className={cn("w-[15px] h-[15px] shrink-0", danger ? "text-bun-danger" : primary ? "text-bun-on-ink" : "text-bun-stone")} />}
          <span className="flex-1 min-w-0 truncate">{children}</span>
          {trailing}
          {shortcut && <Kbd subtle>{shortcut}</Kbd>}
        </button>
      );
  return context ? renderItem({ active: false }) : <HMenu.Item disabled={disabled}>{renderItem}</HMenu.Item>;
}

/** Pointer-anchored menus use the same rows as trigger-based dropdowns. Native
 * form controls keep their own keys, so embedded editors remain usable. */
function Context({ open, onDismiss, anchorRef, children, ...props }) {
  const search = useRef({ value: '', time: 0 });
  return <ContextMenuState.Provider value={{ onDismiss }}>
    <FloatingPanel {...props} open={open} onDismiss={onDismiss} anchorRef={anchorRef} autoFocus tabIndex={-1} role="menu" className="p-[6px] focus:outline-none"
      onKeyDown={event => {
        if (event.target !== event.currentTarget && !event.target.matches('[role="menuitem"]')) return;
        if ((event.key === 'Enter' || event.key === ' ') && event.target.matches('[role="menuitem"]')) {
          event.preventDefault();
          event.target.click();
          return;
        }
        if (event.key === 'Tab') {
          onDismiss?.('tab', event);
          anchorRef?.current?.focus?.();
          return;
        }
        const items = Array.from(event.currentTarget.querySelectorAll('[role="menuitem"]:not(:disabled)'));
        if (!items.length) return;
        const index = items.indexOf(event.target);
        let next;
        if (event.key === 'ArrowDown') next = items[(index + 1) % items.length];
        else if (event.key === 'ArrowUp') next = items[(index < 0 ? items.length - 1 : index - 1 + items.length) % items.length];
        else if (event.key === 'Home') next = items[0];
        else if (event.key === 'End') next = items[items.length - 1];
        else if (event.key.length === 1 && event.key !== ' ' && !event.ctrlKey && !event.metaKey && !event.altKey) {
          const now = Date.now();
          search.current = { value: (now - search.current.time < 500 ? search.current.value : '') + event.key.toLowerCase(), time: now };
          next = [...items.slice(index + 1), ...items.slice(0, index + 1)].find(item => item.textContent.trim().toLowerCase().startsWith(search.current.value));
        }
        if (next) {
          event.preventDefault();
          event.stopPropagation();
          next.focus();
        }
      }}>
      {children}
    </FloatingPanel>
  </ContextMenuState.Provider>;
}

function Section({ as: Component = 'div', children, ...props }) {
  return <Component {...props} className="px-[10px] py-2">{children}</Component>;
}

function Divider() {
  return <div className="h-px bg-bun-line-soft mx-2 my-[5px]" />;
}

Menu.Item = Item;
Menu.Divider = Divider;
Menu.Context = Context;
Menu.Section = Section;
